import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Stethoscope, RefreshCw, IndianRupee } from "lucide-react";

const STATUS = {
  pending_approval: { label: "Pending", className: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", className: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
  suspended: { label: "Suspended", className: "bg-slate-200 text-slate-700" },
};

export default function AdminProfessionalsPage() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending_approval");
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [fee, setFee] = useState("");
  const [note, setNote] = useState("");
  const [feeEditOn, setFeeEditOn] = useState(null);
  const [editingFee, setEditingFee] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/professionals", { params: { status: statusFilter } });
      setItems(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDecide = (item, initial) => {
    setDecideOn(item);
    setDecision(initial);
    setFee(item.consultation_fee ? String(item.consultation_fee) : "");
    setNote("");
  };

  const submitDecision = async () => {
    if (!decideOn) return;
    if (decision === "approved" && (!fee || parseInt(fee, 10) <= 0)) {
      toast.error("Please set a consultation fee before approving.");
      return;
    }
    setSaving(true);
    try {
      const payload = { status: decision, decision_note: note };
      if (decision === "approved") payload.consultation_fee = parseInt(fee, 10);
      await api.patch(`/admin/professionals/${decideOn.id}`, payload);
      toast.success(`Professional ${decision}`);
      setDecideOn(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openFeeEdit = (item) => {
    setFeeEditOn(item);
    setEditingFee(item.consultation_fee ? String(item.consultation_fee) : "");
  };

  const saveFee = async () => {
    if (!feeEditOn) return;
    setSaving(true);
    try {
      await api.patch(`/admin/professionals/${feeEditOn.id}/fee`, { consultation_fee: parseInt(editingFee, 10) });
      toast.success("Fee updated");
      setFeeEditOn(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="admin-professionals-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" data-testid="admin-pros-back" className="text-xs text-slate-500 hover:text-[#2C557E] inline-flex items-center">
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back to dashboard
          </Link>
          <h1 className="font-heading text-3xl text-slate-900 mt-1 flex items-center gap-2">
            <Stethoscope className="w-7 h-7 text-[#5E8B7E]" /> Care Providers
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and approve new dietitians, fellow doctors and allied professionals.
            Approving sets their consultation fee and unlocks their login.
          </p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" data-testid="admin-pros-refresh" className="rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="admin-pros-filter" className="w-56 rounded-xl bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending_approval">Pending approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">
          {items.length} {items.length === 1 ? "professional" : "professionals"}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="admin-pros-empty" className="bg-white rounded-xl border border-dashed border-[#E5EAE8] p-12 text-center text-sm text-slate-500">
          No professionals to show.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const meta = STATUS[it.professional_status] || { label: it.professional_status, className: "bg-slate-100" };
            return (
              <div key={it.id} data-testid={`admin-pro-row-${it.id}`} className="bg-white rounded-2xl border border-[#E5EAE8] p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-lg text-slate-900">{it.name}</h3>
                      <Badge className={`${meta.className} rounded-full text-xs`}>{meta.label}</Badge>
                      {it.verified && <Badge className="bg-blue-50 text-blue-700 rounded-full text-xs">Email verified</Badge>}
                    </div>
                    <p className="text-sm text-slate-700 mt-1"><b>{it.specialty}</b></p>
                    <p className="text-xs text-slate-500">{it.qualification || "—"}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {it.email} · {it.phone || "no phone"} · joined {new Date(it.created_at).toLocaleDateString()}
                    </p>
                    {it.bio && <p className="mt-3 text-sm text-slate-700 bg-[#F4F7F6] rounded-lg p-3">{it.bio}</p>}
                    {it.consultation_fee ? (
                      <p className="text-sm text-slate-700 mt-3 inline-flex items-center gap-1">
                        <IndianRupee className="w-3.5 h-3.5" /> <b>{it.consultation_fee}</b> per consultation
                        <button data-testid={`admin-pro-edit-fee-${it.id}`} onClick={() => openFeeEdit(it)} className="ml-2 text-xs text-[#2C557E] hover:underline">edit</button>
                      </p>
                    ) : it.professional_status === "approved" && (
                      <p className="text-xs text-amber-700 mt-3">Fee not set — <button data-testid={`admin-pro-set-fee-${it.id}`} onClick={() => openFeeEdit(it)} className="text-[#2C557E] hover:underline">set now</button></p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {it.professional_status === "pending_approval" && (
                      <>
                        <Button data-testid={`admin-pro-approve-${it.id}`} size="sm" onClick={() => openDecide(it, "approved")} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Approve</Button>
                        <Button data-testid={`admin-pro-reject-${it.id}`} size="sm" variant="outline" onClick={() => openDecide(it, "rejected")} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">Reject</Button>
                      </>
                    )}
                    {it.professional_status === "approved" && (
                      <Button data-testid={`admin-pro-suspend-${it.id}`} size="sm" variant="outline" onClick={() => openDecide(it, "suspended")} className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50">Suspend</Button>
                    )}
                    {it.professional_status === "suspended" && (
                      <Button data-testid={`admin-pro-reapprove-${it.id}`} size="sm" onClick={() => openDecide(it, "approved")} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Re-approve</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve/Reject/Suspend dialog */}
      <Dialog open={!!decideOn} onOpenChange={(o) => !o && setDecideOn(null)}>
        <DialogContent data-testid="admin-pro-decide-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {decision === "approved" ? "Approve" : decision === "rejected" ? "Reject" : "Suspend"} {decideOn?.name}
            </DialogTitle>
            <DialogDescription>
              {decision === "approved"
                ? "They'll receive an email saying they can now log in."
                : decision === "rejected"
                ? "Their application will be rejected and they cannot log in."
                : "They will be unable to log in or take new appointments."}
            </DialogDescription>
          </DialogHeader>
          {decision === "approved" && (
            <div>
              <Label htmlFor="admin-fee">Consultation fee (₹)</Label>
              <Input id="admin-fee" data-testid="admin-pro-fee-input" inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 500" className="mt-1.5 rounded-xl" />
            </div>
          )}
          <div>
            <Label htmlFor="admin-note">Note to the professional (optional)</Label>
            <Textarea id="admin-note" data-testid="admin-pro-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5 rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOn(null)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="admin-pro-confirm-btn"
              disabled={saving}
              onClick={submitDecision}
              className={`rounded-full ${decision === "approved" ? "bg-[#5E8B7E] hover:bg-[#4A7065]" : decision === "rejected" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
            >
              {saving ? "Saving…" : (decision === "approved" ? "Approve" : decision === "rejected" ? "Reject" : "Suspend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit fee dialog */}
      <Dialog open={!!feeEditOn} onOpenChange={(o) => !o && setFeeEditOn(null)}>
        <DialogContent data-testid="admin-pro-fee-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update fee — {feeEditOn?.name}</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="admin-feeedit">Consultation fee (₹)</Label>
            <Input id="admin-feeedit" data-testid="admin-pro-feeedit-input" inputMode="numeric" value={editingFee} onChange={(e) => setEditingFee(e.target.value.replace(/\D/g, ""))} className="mt-1.5 rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeeEditOn(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="admin-pro-feeedit-save" onClick={saveFee} disabled={saving} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
